import Modal from '@components/Modal'
import { Event } from '@types'
import dayjs from 'dayjs'
import React from 'react'
import styled from 'styled-components'

interface ViewEventModalProps {
	isOpen: boolean
	onClose: () => void
	event: Event
}

const Content = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
`

const Label = styled.div`
	font-size: 0.75rem;
	font-weight: 600;
	color: #6b7280;
	text-transform: uppercase;
	letter-spacing: 0.03em;
`

const Value = styled.div`
	font-size: 0.95rem;
	color: #111827;
	line-height: 1.5;
	white-space: pre-wrap;
	word-break: break-word;
`

const LinkList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.35rem;
`

const LinkItem = styled.a`
	color: #2563eb;
	text-decoration: none;
	word-break: break-all;

	&:hover {
		text-decoration: underline;
	}
`

const formatTime = (timeString?: string | null): string => {
	if (!timeString) return ''
	try {
		return dayjs(`2000-01-01T${timeString}`).format('h:mm A')
	} catch {
		return timeString
	}
}

const EmptyState = styled.div`
	text-align: center;
	color: #6b7280;
	font-size: 0.95rem;
	padding: 0.5rem 0;
`

const ViewEventModal: React.FC<ViewEventModalProps> = ({ isOpen, onClose, event }) => {
	const hasDetails =
		event.startTime ||
		event.description ||
		event.location?.primaryText ||
		(event.tripEventLinks && event.tripEventLinks.length > 0)

	return (
		<Modal isOpen={isOpen} onClose={onClose} title={event.name} closeOnBackdropClick={true}>
			<Content>
				{!hasDetails && (
					<EmptyState>There is no other information on this event</EmptyState>
				)}

				{event.startTime && (
					<div>
						<Label>Time</Label>
						<Value>{formatTime(event.startTime)}</Value>
					</div>
				)}

				{event.description && (
					<div>
						<Label>Description</Label>
						<Value>{event.description}</Value>
					</div>
				)}

				{event.location?.primaryText && (
					<div>
						<Label>Location</Label>
						<Value>{event.location.primaryText}</Value>
					</div>
				)}

				{event.tripEventLinks && event.tripEventLinks.length > 0 && (
					<div>
						<Label>Links</Label>
						<LinkList>
							{event.tripEventLinks.map(link => {
								const hasProtocol =
									link.url.startsWith('http://') || link.url.startsWith('https://')
								const looksLikeUrl = link.url.includes('.') && !link.url.includes(' ')
								const hrefUrl =
									!hasProtocol && looksLikeUrl ? `https://${link.url}` : link.url
								const isClickable = hasProtocol || looksLikeUrl

								return isClickable ? (
									<LinkItem
										key={link.id}
										href={hrefUrl}
										target="_blank"
										rel="noopener noreferrer"
									>
										{link.url}
									</LinkItem>
								) : (
									<Value key={link.id}>{link.url}</Value>
								)
							})}
						</LinkList>
					</div>
				)}
			</Content>
		</Modal>
	)
}

export default ViewEventModal
